import React from 'react';
import { cn } from '@/lib/utils';

interface StepProgressProps {
  /**
   * Progress in steps. Whole numbers fill that many bars (2 of 6 → two full bars); a fraction
   * part-fills the next one, e.g. 0.6 for "under way on step 1" (Figma "Find your plan").
   */
  current: number;
  total: number;
  /** Name of the current step, shown as "Step 1 of 6 · Reason". Leave out for bars only. */
  label?: string;
  className?: string;
}

/** One bar per step, with an optional "Step 1 of 6 · Reason" line (Figma "Progress steps"). */
const StepProgress: React.FC<StepProgressProps> = ({ current, total, label, className }) => (
  <div className={cn('tw-flex tw-flex-col tw-gap-2', className)}>
    {label && (
      <p className="tw-text-[13px] tw-font-semibold tw-leading-[18px] tw-text-ds-text-primary">
        Step {Math.ceil(current)} of {total} · {label}
      </p>
    )}
    <div
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuenow={current}
      aria-label={label ? 'Progress' : `Step ${Math.ceil(current)} of ${total}`}
      className="tw-flex tw-gap-1"
    >
      {Array.from({ length: total }, (_, i) => (
        <span key={i} className="tw-h-1 tw-flex-1 tw-overflow-hidden tw-rounded-sm tw-bg-ds-border-default">
          <span
            className="tw-block tw-h-full tw-rounded-sm tw-bg-ds-brand tw-transition-[width] tw-duration-500 tw-ease-out"
            style={{ width: `${Math.min(1, Math.max(0, current - i)) * 100}%` }}
          />
        </span>
      ))}
    </div>
  </div>
);

export default StepProgress;
